import React from 'react'
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from '../../components/navigation-menu'
import { cn } from '../../lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'

export interface UINavigationMenuItemProps {
  title: string
  href?: string
  icon?: React.ComponentType<{ className?: string }>
  isActive?: boolean
  disabled?: boolean
  children?: UINavigationMenuItemProps[]
  content?: React.ReactNode
}

const navigationMenuVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0", // Width Stability 패턴
  {
    variants: {
      variant: {
        default: "",
        minimal: "bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface UINavigationMenuProps extends
  React.ComponentProps<typeof NavigationMenu>,
  VariantProps<typeof navigationMenuVariants> {
  /**
   * 네비게이션 메뉴 아이템들
   */
  items?: UINavigationMenuItemProps[]
  /**
   * 메뉴 방향
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * viewport 표시 여부
   */
  viewport?: boolean
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
}

export interface UINavigationMenuListProps extends React.ComponentProps<typeof NavigationMenuList> {
  variant?: 'default' | 'minimal'
  orientation?: 'horizontal' | 'vertical'
}

export interface UINavigationMenuTriggerProps extends React.ComponentProps<typeof NavigationMenuTrigger> {
  icon?: React.ComponentType<{ className?: string }>
  isActive?: boolean
}

export interface UINavigationMenuLinkProps extends React.ComponentProps<typeof NavigationMenuLink> {
  icon?: React.ComponentType<{ className?: string }>
  isActive?: boolean
}

/**
 * 네비게이션 메뉴 컴포넌트
 * 복잡한 메뉴 구조와 드롭다운을 지원합니다.
 */
export const UINavigationMenu = React.forwardRef<
  React.ComponentRef<typeof NavigationMenu>,
  UINavigationMenuProps
>(({
  className,
  items,
  variant,
  orientation = "horizontal",
  viewport = true,
  containerClassName,
  children,
  ...props
}, ref) => {
  const renderMenuItem = (item: UINavigationMenuItemProps, index: number) => {
    const hasChildren = item.children && item.children.length > 0
    const hasContent = !!item.content

    if (hasChildren || hasContent) {
      // 드롭다운 메뉴 아이템
      return (
        <NavigationMenuItem key={`${item.title}-${index}`}>
          <UINavigationMenuTrigger
            icon={item.icon}
            isActive={item.isActive}
            disabled={item.disabled}
          >
            {item.title}
          </UINavigationMenuTrigger>
          <UINavigationMenuContent>
            {hasContent ? (
              item.content
            ) : (
              <UIBox className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                {item.children!.map((child, childIndex) => (
                  <UINavigationMenuLink
                    key={`${child.title}-${childIndex}`}
                    href={child.href}
                    icon={child.icon}
                    isActive={child.isActive}
                    className="select-none rounded-lg leading-none no-underline outline-none"
                  >
                    {child.title}
                  </UINavigationMenuLink>
                ))}
              </UIBox>
            )}
          </UINavigationMenuContent>
        </NavigationMenuItem>
      )
    } else {
      // 일반 링크 아이템
      return (
        <NavigationMenuItem key={`${item.title}-${index}`}>
          <UINavigationMenuLink
            href={item.href}
            icon={item.icon}
            isActive={item.isActive}
            className={cn(
              item.disabled && "opacity-50 pointer-events-none"
            )}
          >
            {item.title}
          </UINavigationMenuLink>
        </NavigationMenuItem>
      )
    }
  }

  if (!items?.length && !children) return null

  if (items) {
    return (
      <UIBox className={cn(
        "w-full max-w-full min-w-0 flex-shrink-0",
        orientation === 'vertical' && "flex flex-col",
        containerClassName
      )}>
        <NavigationMenu
          ref={ref}
          orientation={orientation}
          viewport={viewport}
          className={cn(navigationMenuVariants({ variant }), className)}
          {...props}
        >
          <UINavigationMenuList
            variant={variant || "default"}
            orientation={orientation}
          >
            {items.map(renderMenuItem)}
          </UINavigationMenuList>
        </NavigationMenu>
      </UIBox>
    )
  }

  return (
    <NavigationMenu
      ref={ref}
      orientation={orientation}
      viewport={viewport}
      className={cn(navigationMenuVariants({ variant }), className)}
      {...props}
    >
      {children}
    </NavigationMenu>
  )
})

// Custom style for navigation menu list
const navigationMenuListVariants = cva(
  "group flex list-none items-center justify-center",
  {
    variants: {
      orientation: {
        horizontal: "gap-1",
        vertical: "flex-col items-stretch gap-0.5 w-full"
      },
      variant: {
        default: "",
        minimal: "bg-transparent"
      }
    },
    defaultVariants: {
      orientation: "horizontal",
      variant: "default"
    }
  }
)

export const UINavigationMenuList = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuList>,
  UINavigationMenuListProps
>(({
  className,
  variant = "default",
  orientation = "horizontal",
  ...props
}, ref) => (
  <NavigationMenuList
    ref={ref}
    className={cn(
      navigationMenuListVariants({ orientation, variant }),
      className
    )}
    {...props}
  />
))

export const UINavigationMenuTrigger = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuTrigger>,
  UINavigationMenuTriggerProps
>(({
  className,
  icon: Icon,
  isActive,
  children,
  ...props
}, ref) => (
  <NavigationMenuTrigger
    ref={ref}
    className={cn(
      navigationMenuTriggerStyle(),
      isActive && "bg-primary text-primary-foreground font-semibold shadow-sm",
      className
    )}
    data-active={isActive}
    {...props}
  >
    {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
    {children}
  </NavigationMenuTrigger>
))

export const UINavigationMenuLink = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuLink>,
  UINavigationMenuLinkProps
>(({
  className,
  icon: Icon,
  isActive,
  children,
  ...props
}, ref) => (
  <NavigationMenuLink
    ref={ref}
    className={cn(
      navigationMenuTriggerStyle(),
      isActive && "bg-primary text-primary-foreground font-semibold shadow-sm",
      className
    )}
    data-active={isActive}
    {...props}
  >
    {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
    {children}
  </NavigationMenuLink>
))

export const UINavigationMenuContent = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuContent>,
  React.ComponentProps<typeof NavigationMenuContent>
>(({ className, ...props }, ref) => (
  <NavigationMenuContent
    ref={ref}
    className={cn("w-full max-w-full min-w-0 flex-shrink-0", className)}
    {...props}
  />
))

// Re-export components for flexibility
export const UINavigationMenuItem = NavigationMenuItem
export const UINavigationMenuIndicator = NavigationMenuIndicator
export const UINavigationMenuViewport = NavigationMenuViewport

// Display names
UINavigationMenu.displayName = "UINavigationMenu"
UINavigationMenuList.displayName = "UINavigationMenuList"
UINavigationMenuTrigger.displayName = "UINavigationMenuTrigger"
UINavigationMenuLink.displayName = "UINavigationMenuLink"
UINavigationMenuContent.displayName = "UINavigationMenuContent"